import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { isRequired } from './validation';

export function useDebounce(value = '', delay = 1000) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };

  }, [value]);

  return debouncedValue;
}

export function useDropdown(data, initialValues) {
  const [values, setValues] = useState(data);
  const numOfItem = Object.keys(data).length;

  const isValid = useMemo(() => {
    const onlyValue = [
      ...new Set(Object.values(values).map((item) => item.value)),
    ];
    return onlyValue.length >= numOfItem
      ? onlyValue.every((value) => Boolean(value))
      : false;
  }, [values]);

  const handleBlur = (e, key) => {
    const { value, placeholder } = e.target;
    const name = placeholder.split(' ')[1];

    setValues({
      ...values,
      [key]: {
        ...values[key],
        touched: true,
        error: isRequired(value, `${name} harus dipilih`),
      },
    });
  };

  const handleChange = (value, key, resetKeys = []) => {
    const newValues = {
      ...values,
      [key]: { ...values[key], error: '', value },
    };
    resetKeys.forEach((key) => {
      const isAvailableKey = Object.keys(newValues).includes(key);
      if (isAvailableKey) {
        newValues[key].value = '';
      }
    });
    setValues(newValues);
  };

  useEffect(() => {
    if (Object.keys(initialValues).length) {
      const keys = Object.keys(values);
      const initialData = Object.entries(initialValues).map(([key, value]) => {
        if (keys.includes(key)) {
          return {
            [key]: {
              error: '',
              touched: false,
              value: value?.toLowerCase() === 'all' ? '' : value,
            },
          };
        }
      });

      setValues(Object.assign({}, ...initialData));
    }
  }, [initialValues]);

  return { values, handleBlur, handleChange, isValid };
}

export const useScheduledPolling = ({
  action,          // The function to execute (e.g., dispatch fetch)
  onComplete,      // Optional: Function to run when time is up
  interval = 5000, // Fetch every 5 seconds
  duration = 30000 // Stop after 30 seconds
}) => {
  const [secondsElapsed, setSecondsElapsed] = useState(0);
  const [isPolling, setIsPolling] = useState(false);

  const actionRef = useRef(action);
  const onCompleteRef = useRef(onComplete);
  const payloadRef = useRef(null);

  useEffect(() => {
    actionRef.current = action;
    onCompleteRef.current = onComplete;
  }, [action, onComplete]);

  const startPolling = useCallback((payload) => {
    payloadRef.current = payload;
    setSecondsElapsed(0);
    setIsPolling(true);

    if (actionRef.current) actionRef.current(payload);
  }, []);

  const stopPolling = useCallback(() => {
    setIsPolling(false);
    setSecondsElapsed(0);
  }, []);

  useEffect(() => {
    let timerInterval = null;

    if (isPolling) {
      timerInterval = setInterval(() => {
        setSecondsElapsed((prevSeconds) => {
          const nextSeconds = prevSeconds + 1;
          if (nextSeconds >= duration / 1000) {
            setIsPolling(false);
            if (onCompleteRef.current) onCompleteRef.current();
            return nextSeconds;
          }
          if ((nextSeconds * 1000) % interval === 0) {
            if (actionRef.current) actionRef.current(payloadRef.current);
          }

          return nextSeconds;
        });
      }, 1000);
    }

    return () => {
      if (timerInterval) clearInterval(timerInterval);
    };
  }, [isPolling, duration, interval]);

  return { startPolling, stopPolling, isPolling, secondsElapsed };
};

export const triggerDownload = (url, fileName) => {
  if (!url) return;

  const link = document.createElement('a');
  link.href = url;
  if (fileName) {
    link.setAttribute('download', fileName);
  } else {
    link.setAttribute('download', 'download');
  }
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const fetchThenDownload = async (url, fileName) => {
  if (!url) return;

  try {
    const response = await fetch(url);
    if (!response.ok) throw new Error('Network response was not ok');

    const blob = await response.blob();

    const blobUrl = window.URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = blobUrl;
    link.setAttribute('download', fileName || 'download.pdf');

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    window.URL.revokeObjectURL(blobUrl);

  } catch (error) {
    return error;
  }
};



export const useFileDownloader = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const downloadFile = useCallback(async (url, fileName) => {
    if (!url) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', fileName || 'download'); // Default name if none provided

      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      setError(err.message || 'Failed to download file');
    } finally {
      setIsLoading(false);
    }
  }, []);

  return { downloadFile, isLoading, error };
};
